import {
  CascadiaExpandableCardProps,
  ExpandableCardStyles,
} from "@certn/cascadia/components/card/types";
import { css, cx } from "@emotion/css";
import { useEffect, useState } from "react";

import { Card } from "./Card";
import { Space } from "../../foundations/grid-layout/Layout";
import { Stack } from "../../foundations/grid-layout/Stack";
import { Text } from "../../foundations/typography/Text";
import { GhostIconButton } from "../button/GhostIconButton";
import { Divider } from "../divider/Divider";
import { Icon } from "@certn/cascadia-react";

/**
 * A card that can be expanded to show more content.
 */
export function ExpandableCard(props: CascadiaExpandableCardProps) {
  const {
    title,
    titleStyle = "global/heading-large",
    iconLabel,
    subtitle,
    expandable = props.children !== undefined,
    expanded,
    rightContent,
    children,
    onToggle,
    cardStyles,
    border = false,
  } = props;
  const [_expanded, setExpanded] = useState(expanded);
  const [colorPalette, setColorPalette] = useState<
    ExpandableCardStyles | undefined
  >(
    cardStyles
      ? expanded
        ? cardStyles?.expandedStyles
        : cardStyles?.collapsedStyles
      : undefined
  );

  useEffect(() => {
    setExpanded(expanded);
  }, [expanded]);

  useEffect(() => {
    if (cardStyles) {
      setColorPalette(
        _expanded ? cardStyles.expandedStyles : cardStyles.collapsedStyles
      );
    }
  }, [cardStyles, expanded, _expanded]);

  return (
    <Card type="large" background={colorPalette?.backgroundColor}>
      <Stack
        gap="space-0"
        className={css({
          padding: "var(--space-4)",
          ...(border && {
            padding: "var(--space-3)",
            borderTop: "1px solid var(--border-base)",
            borderLeft: "1px solid var(--border-base)",
            borderRight: "1px solid var(--border-base)",
            borderRadius: "var(--border-radius-3)",
          }),
        })}
        background={colorPalette?.titleBackgroundColor}
      >
        <Stack
          orientation="horizontal"
          align="center"
          gap="space-3"
          className={styles.heading}
        >
          {props.titleIcon && <Icon name={props.titleIcon} />}
          {typeof title === "string" ? (
            <Text style={titleStyle} color={colorPalette?.titleTextColor}>
              {title}
            </Text>
          ) : (
            title
          )}
          <Space />
          {rightContent}
          {expandable && (
            <Stack orientation="horizontal" gap="space-1">
              <Text
                style="form/body-secondary"
                color={colorPalette?.titleTextColor}
              >
                {iconLabel}
              </Text>
              <GhostIconButton
                icon="expand_more"
                color={colorPalette?.titleTextColor}
                onPress={() => {
                  setExpanded((prev) => !prev);
                  onToggle?.();
                }}
                className={cx(
                  styles.showDetailsbutton,
                  _expanded && styles.showDetailsActive
                )}
              />
            </Stack>
          )}
        </Stack>
        {subtitle && (
          <Stack orientation="horizontal" gap="space-2">
            {typeof subtitle === "string" ? (
              <Text
                style="form/body-secondary"
                color={colorPalette?.titleTextColor || "text-weak"}
              >
                {subtitle}
              </Text>
            ) : (
              subtitle
            )}
          </Stack>
        )}
      </Stack>
      {_expanded && <Divider />}
      {_expanded && <div className={styles.children}>{children}</div>}
    </Card>
  );
}

const styles = {
  heading: css({ minHeight: "40px" }),
  showDetailsbutton: css({
    border: "none",
    outline: "none",
    "&:focus-within": {
      boxShadow: "none",
    },
    "& i.material-symbols-rounded": {
      transform: "rotate(0deg)",
      transition: "transform 0.2s ease-out",
    },
    "@media print": { display: "none" },
  }),
  showDetailsActive: css({
    "& i.material-symbols-rounded": {
      transform: "rotate(180deg)",
    },
  }),
  children: css({
    marginTop: "var(--space-4)",
    paddingBottom: "var(--space-5)",
  }),
};
